import { useEffect, useState, type ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { api } from '../lib/api'
import { biz } from '../lib/business'
import { useAuth } from './AuthContext'

// Eight entries, ordered by how often they are used. The invoice inbox is
// first and is the landing page: uploading a document and watching it get read
// is the thing this product does.
const NAV = [
  { to: '/inbox', label: 'Upload & review', group: 'Invoices', badge: true },
  { to: '/bills', label: 'Stored bills', group: 'Invoices' },

  { to: '/sales', label: 'Sales', group: 'Business' },
  { to: '/pipeline', label: 'Leads', group: 'Business' },
  { to: '/contacts', label: 'Contacts', group: 'Business' },
  { to: '/products', label: 'Products', group: 'Business' },

  { to: '/reports', label: 'Reports', group: 'Insight' },
  { to: '/settings', label: 'Company', group: 'Insight' },
]

function ThemeToggle() {
  const [theme, setTheme] = useState<'light' | 'dark' | null>(
    () => (localStorage.getItem('theme') as 'light' | 'dark') ?? null,
  )

  useEffect(() => {
    if (theme) document.documentElement.setAttribute('data-theme', theme)
    else document.documentElement.removeAttribute('data-theme')
  }, [theme])

  return (
    <button className="btn btn-ghost btn-sm"
      onClick={() => {
        const next = theme === 'dark' ? 'light' : 'dark'
        setTheme(next)
        localStorage.setItem('theme', next)
      }}
      aria-label="Switch theme" title="Switch theme">
      {theme === 'dark' ? '☾' : '☀'}
    </button>
  )
}

export default function Shell({ children }: { children: ReactNode }) {
  const { me, logout } = useAuth()
  const location = useLocation()
  const [pending, setPending] = useState(0)
  const [gstMissing, setGstMissing] = useState(false)
  // Only meaningful on narrow screens, where the sidebar becomes a drawer.
  const [navOpen, setNavOpen] = useState(false)

  // Navigating is the natural "done with the menu" signal on a phone.
  useEffect(() => { setNavOpen(false) }, [location.pathname])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setNavOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    let alive = true
    const load = async () => {
      try {
        const { items } = await api.listDocuments({ status: 'needs_review' })
        if (alive) setPending(items.length)
      } catch { /* the badge is not worth an error toast */ }
    }
    load()
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') load()
    }, 60000)
    return () => { alive = false; window.clearInterval(timer) }
  }, [location.pathname])

  useEffect(() => {
    biz.company().then((c) => setGstMissing(!c.gst_configured)).catch(() => {})
  }, [location.pathname])

  const groups = [...new Set(NAV.map((n) => n.group))]
  const title = [...NAV]
    .sort((a, b) => b.to.length - a.to.length)
    .find((n) => location.pathname.startsWith(n.to))?.label ?? 'Review'

  return (
    <div className={`shell${navOpen ? ' nav-open' : ''}`}>
      <div className="nav-scrim" onClick={() => setNavOpen(false)} aria-hidden="true" />

      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark">₹</span>
          <span>Invoice&nbsp;IQ</span>
        </div>

        <nav className="nav">
          {groups.map((group) => (
            <div key={group}>
              <div className="nav-label">{group}</div>
              {NAV.filter((n) => n.group === group).map((item) => (
                <NavLink key={item.to} to={item.to}
                  className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
                  <span>{item.label}</span>
                  {item.badge && pending > 0 && (
                    <span className="nav-count">{pending}</span>
                  )}
                  {item.to === '/settings' && gstMissing && (
                    <span className="nav-count"
                      style={{ background: 'var(--warn)', color: 'var(--surface)' }}>!</span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-foot">
          <div style={{ fontSize: 12.5, fontWeight: 560 }}>{me?.org.name}</div>
          <div className="muted" style={{ fontSize: 11.5, marginBottom: 8 }}>
            {me?.user.username ?? me?.user.email} · {me?.role}
          </div>
          <button className="btn btn-sm" style={{ width: '100%' }} onClick={logout}>
            Sign out
          </button>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <button className="btn btn-ghost btn-sm nav-toggle"
            onClick={() => setNavOpen((v) => !v)}
            aria-label="Menu" aria-expanded={navOpen}>☰</button>
          <h1>{title}</h1>
          <div className="spacer" />
          <ThemeToggle />
        </header>
        <div className="content">
          <div className="content-narrow">{children}</div>
        </div>
      </div>
    </div>
  )
}
